import React, { useEffect, useState } from 'react';

import { Paper, Stack } from '@mui/material';

import type { ManualApprovalState } from '../types';
import type { DatesRestrictionsChecklistRow } from '../utils';

import ChecklistRuleCard from './ChecklistRuleCard';
import HiddenApprovedFooter from './HiddenApprovedFooter';

interface Props {
  hiddenApprovedCount: number;
  manualApprovals: ManualApprovalState;
  rows: Array<DatesRestrictionsChecklistRow>;
  showAutoApproved: boolean;
  onApprovalChange: (rowId: string) => void;
}

export default function DatesRestrictionsReportCard({
  hiddenApprovedCount,
  manualApprovals,
  rows,
  showAutoApproved,
  onApprovalChange,
}: Props) {
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});

  useEffect(() => {
    setExpandedRows(rows.reduce<Record<string, boolean>>((nextRows, row) => ({ ...nextRows, [row.id]: true }), {}));
  }, [rows]);

  return (
    <Stack spacing={1.5}>
      {rows.length === 0 ? (
        <Paper variant="outlined" sx={{ color: 'text.secondary', p: 4, textAlign: 'center' }}>
          No Dates &amp; Restrictions rows match this view.
        </Paper>
      ) : (
        rows.map((row) => {
          const manuallyApproved = !!manualApprovals[row.id];
          const approved = row.status === 'approved' || manuallyApproved;
          const isExpanded = expandedRows[row.id] ?? true;

          return (
            <ChecklistRuleCard
              key={row.id}
              approved={approved}
              dataPoints={row.details.map((detail) => ({
                label: detail.label,
                readOnly: true,
                value: detail.value,
              }))}
              expanded={isExpanded}
              manuallyApproved={manuallyApproved}
              rule={row.rule}
              summary={row.summary}
              title={row.title}
              onApprovalChange={() => onApprovalChange(row.id)}
              onToggleExpanded={() => setExpandedRows((currentRows) => ({ ...currentRows, [row.id]: !isExpanded }))}
            />
          );
        })
      )}
      <HiddenApprovedFooter
        count={hiddenApprovedCount}
        noun="Dates & Restrictions row"
        showAutoApproved={showAutoApproved}
      />
    </Stack>
  );
}
